import type { Middleware } from "@flare/flare_server_client";
import posthog, { type CapturedNetworkRequest, type Headers as PostHogHeaders } from "posthog-js";

export const POSTHOG_SESSION_ID_HEADER = "X-PostHog-Session-Id";
export const POSTHOG_DISTINCT_ID_HEADER = "X-PostHog-Distinct-Id";
export const TRACE_ID_RESPONSE_HEADER = "X-Trace-Id";

const TRACE_ID_RESPONSE_HEADER_LOWER = TRACE_ID_RESPONSE_HEADER.toLowerCase();

let lastTraceId: string | undefined;

export function getLastTraceId(): string | undefined {
  return lastTraceId;
}

function pickTraceIdHeader(headers: PostHogHeaders | undefined): PostHogHeaders | undefined {
  if (!headers) {
    return undefined;
  }
  for (const [name, value] of Object.entries(headers)) {
    if (name.toLowerCase() === TRACE_ID_RESPONSE_HEADER_LOWER && value) {
      return { [TRACE_ID_RESPONSE_HEADER_LOWER]: value };
    }
  }
  return {};
}

/**
 * Keep only the Tempo correlation response header in session-replay network
 * capture. Request headers are dropped entirely.
 */
export function maskCapturedNetworkRequestForTraceId(
  request: CapturedNetworkRequest,
): CapturedNetworkRequest {
  return {
    ...request,
    requestHeaders: undefined,
    responseHeaders: pickTraceIdHeader(request.responseHeaders),
  };
}

function withHeader(headers: HeadersInit | undefined, name: string, value: string): Headers {
  const next = new Headers(headers);
  next.set(name, value);
  return next;
}

/**
 * OpenAPI client middleware: send PostHog session/distinct ids on requests and
 * remember the latest backend `X-Trace-Id` for error-boundary captures.
 *
 * Per-request trace ids for session replay come from PostHog network header
 * capture of the CORS-exposed `x-trace-id` response header — not a session
 * property (a session has many API traces).
 */
export const posthogCorrelationMiddleware: Middleware = {
  async pre(context) {
    if (!import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
      return;
    }

    let headers = context.init.headers;
    const sessionId = posthog.get_session_id();
    if (sessionId) {
      headers = withHeader(headers, POSTHOG_SESSION_ID_HEADER, sessionId);
    }
    const distinctId = posthog.get_distinct_id();
    if (distinctId) {
      headers = withHeader(headers, POSTHOG_DISTINCT_ID_HEADER, distinctId);
    }

    return {
      url: context.url,
      init: {
        ...context.init,
        headers,
      },
    };
  },

  async post(context) {
    const traceId = context.response.headers.get(TRACE_ID_RESPONSE_HEADER);
    if (traceId) {
      lastTraceId = traceId;
    }
  },
};
